import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Conversation, Message, User } from '../types';
import { ArrowLeft, Send, Building2, Briefcase } from 'lucide-react';
import { formatMessageTime, formatMessageDate, groupMessagesByDate } from '../utils/formatters';
import { ConnectionService } from '../services/connectionService';
import { ROUTES } from '../constants/app';

interface ChatViewProps {
  conversation: Conversation;
  currentUser: User;
  onSendMessage: (content: string) => void;
}

export const ChatView: React.FC<ChatViewProps> = ({
  conversation,
  currentUser,
  onSendMessage
}) => {
  const navigate = useNavigate();
  const [newMessage, setNewMessage] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Manejo seguro para obtener el otro usuario
  let otherUser: User;
  try {
    if (conversation.participants.length >= 2) {
      otherUser = ConnectionService.getOtherUser(conversation, currentUser.id);
    } else {
      // Crear usuario temporal si no hay datos completos
      otherUser = {
        id: 'temp-user',
        name: 'Usuario',
        age: 30,
        location: 'Ubicación',
        userType: 'entrepreneur',
        images: ['https://images.pexels.com/photos/1043471/pexels-photo-1043471.jpeg?auto=compress&cs=tinysrgb&w=400&h=600&fit=crop']
      } as User;
    }
  } catch (error) {
    console.error('❌ Error al obtener otro usuario:', error);
    otherUser = {
      id: 'temp-user',
      name: 'Usuario',
      age: 30,
      location: 'Ubicación',
      userType: 'entrepreneur',
      images: ['https://images.pexels.com/photos/1043471/pexels-photo-1043471.jpeg?auto=compress&cs=tinysrgb&w=400&h=600&fit=crop']
    } as User;
  }

  const handleBack = () => {
    navigate(ROUTES.CONVERSATIONS);
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [conversation.messages]);

  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    if (newMessage.trim()) {
      onSendMessage(newMessage.trim());
      setNewMessage('');
      inputRef.current?.focus();
    }
  };

  const messageGroups = groupMessagesByDate(conversation.messages);

  return (
    <div className="flex flex-col w-full h-full bg-white">
      {/* Header */}
      <header className="flex items-center px-4 py-3 bg-white border-b border-gray-100 shadow-sm flex-shrink-0">
        <div className="flex items-center gap-3">
          <button
            onClick={handleBack}
            className="p-2 rounded-full hover:bg-gray-100 transition-colors duration-200"
          >
            <ArrowLeft className="w-6 h-6 text-gray-600" />
          </button>
          
          <div className="flex items-center gap-3">
            <img
              src={otherUser.images[0]}
              alt={otherUser.name}
              className="w-10 h-10 rounded-full object-cover object-center"
            />
            <div className="space-y-1">
              <h2 className="font-semibold text-gray-900">{otherUser.name}</h2>
              <div className="flex items-center gap-2">
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                  otherUser.userType === 'entrepreneur' 
                    ? 'bg-green-100 text-green-700' 
                    : 'bg-blue-100 text-blue-700'
                }`}>
                  {otherUser.userType === 'entrepreneur' ? 'Entrepreneur' : 'Investor'}
                </span>
                {otherUser.userType === 'entrepreneur' ? (
                  <div className="flex items-center gap-1 text-xs text-gray-500">
                    <Building2 className="w-3 h-3" />
                    <span>{(otherUser as any).company}</span>
                  </div>
                ) : (
                  <div className="flex items-center gap-1 text-xs text-gray-500">
                    <Briefcase className="w-3 h-3" />
                    <span className="capitalize">{(otherUser as any).investorType}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Messages */}
      <main className="flex-1 overflow-y-auto p-4 space-y-4">
        {messageGroups.map(([dateKey, messages]) => (
          <div key={dateKey}>
            {/* Date separator */}
            <div className="flex justify-center mb-6">
              <span className="bg-gray-100 text-gray-600 text-xs px-3 py-1 rounded-full">
                {formatMessageDate(new Date(dateKey))}
              </span>
            </div>
            
            {/* Messages for this date */}
            <div className="space-y-4">
              {messages.map((message) => {
                const isCurrentUser = message.senderId === currentUser.id;
                
                return (
                  <div
                    key={message.id}
                    className={`flex ${isCurrentUser ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`max-w-xs lg:max-w-md px-4 py-2 rounded-2xl ${
                      isCurrentUser
                        ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white'
                        : 'bg-gray-100 text-gray-900'
                    }`}>
                      <p className="text-sm leading-relaxed">{message.content}</p>
                      <p className={`text-xs mt-2 ${
                        isCurrentUser ? 'text-blue-100' : 'text-gray-500'
                      }`}>
                        {formatMessageTime(message.timestamp)}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </main>

      {/* Message Input */}
      <footer className="border-t border-gray-100 p-4">
        <form onSubmit={handleSendMessage} className="flex items-center gap-3">
          <input
            ref={inputRef}
            type="text"
            value={newMessage}
            onChange={(e) => setNewMessage(e.target.value)}
            placeholder="Type a message..."
            className="flex-1 px-4 py-3 border border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
          />
          <button
            type="submit"
            disabled={!newMessage.trim()}
            className={`p-3 rounded-full transition-all ${
              newMessage.trim()
                ? 'bg-gradient-to-r from-blue-500 to-purple-600 text-white hover:scale-105 shadow-lg'
                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }`}
          >
            <Send className="w-5 h-5" />
          </button>
        </form>
      </footer>
    </div>
  );
};